<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("departments.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("departments.error") }}</div>
    <template v-else>
      <!-- page head + primary add -->
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("departments.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("departments.title") }}</div>
        </div>
        <button class="btn primary" type="button" data-test="dept-add" @click="goAdd">➕ {{ $t("departments.addBtn") }}</button>
      </div>

      <!-- list panel -->
      <div class="panel">
        <div class="toolbar">
          <div class="searchbar grow">
            🔎
            <input
              class="input"
              style="border: 0; background: transparent; box-shadow: none; padding: 0"
              :placeholder="$t('departments.searchPlaceholder')"
              v-model="q"
              @input="reload"
            />
          </div>
          <!-- C2 «الفلاتر تبقا dropdown» -->
          <div class="filters" data-test="filters">
            <button
              class="btn soft sm"
              type="button"
              data-test="filters-toggle"
              :aria-expanded="String(filtersOpen)"
              @click.stop="filtersOpen = !filtersOpen"
            >
              ⚙️ {{ $t("departments.filter.button") }}<template v-if="filterCount"> ({{ fInt(filterCount) }})</template>
            </button>
            <div v-if="filtersOpen" class="filters__panel" data-test="filters-panel" @click.stop>
              <label class="filters__row">
                <span class="xs muted">{{ $t("departments.colStatus") }}</span>
                <select class="select" v-model="filter" data-test="status-filter" @change="reload">
                  <option value="all">{{ $t("departments.filterAll") }}</option>
                  <option value="active">{{ $t("departments.filterActive") }}</option>
                  <option value="suspended">{{ $t("departments.filterSuspended") }}</option>
                </select>
              </label>
              <div class="row between filters__foot">
                <button class="btn ghost sm" type="button" data-test="filters-clear" :disabled="!filterCount" @click="clearFilters">
                  {{ $t("departments.filter.clear") }}
                </button>
                <button class="btn soft sm" type="button" data-test="filters-done" @click="filtersOpen = false">
                  {{ $t("departments.filter.done") }}
                </button>
              </div>
            </div>
          </div>
          <button class="btn primary sm" type="button" @click="goAdd">➕ {{ $t("departments.addBtn") }}</button>
        </div>

        <!-- outcome banners: the form now lives on another page, so the list is
             where the user learns what happened when they get back. -->
        <div v-if="savedNotice" class="note info" data-test="saved-note">
          ✓ {{ $t("departments.saved." + savedNotice.kind, { name: savedNotice.name }) }}
        </div>
        <div v-if="deletedName" class="note info" data-test="deleted-note">
          ✓ {{ $t("departments.delete.done", { name: deletedName }) }}
        </div>
        <div v-if="deleteError" class="note" data-test="delete-error">
          {{ $t("departments.delete.errors." + deleteError.key, deleteError.params) }}
        </div>

        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>{{ $t("departments.colDept") }}</th>
                <th>{{ $t("departments.colEmployees") }}</th>
                <th>{{ $t("departments.colManager") }}</th>
                <th>{{ $t("departments.colStatus") }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="rows.length === 0">
                <td colspan="5" class="muted" style="text-align: center; padding: 22px">
                  {{ $t("departments.empty") }}
                </td>
              </tr>
              <tr v-for="r in rows" :key="r.id">
                <td><b>{{ r.nameAr }}</b></td>
                <td class="mono">{{ fInt(r.employeeCount) }}</td>
                <td>
                  <div class="u" v-if="r.managerName">
                    <span class="avatar" :class="r.avatarClass">{{ r.initials }}</span>
                    {{ r.managerName }}
                  </div>
                  <span v-else class="muted">{{ $t("departments.noManager") }}</span>
                </td>
                <td>
                  <span class="tag" :class="r.statusTag">
                    <span class="dotc"></span> {{ statusLabel(r.statusKey) }}
                  </span>
                </td>
                <td>
                  <div class="row" style="gap: 6px; justify-content: flex-end">
                    <button class="btn ghost sm" type="button" :data-test="'dept-edit-' + r.id" @click="goEdit(r)">{{ $t("departments.actEdit") }}</button>
                    <button
                      v-if="r.isActive"
                      class="btn soft sm"
                      type="button"
                      @click="toggleActive(r)"
                    >
                      {{ $t("departments.actSuspend") }}
                    </button>
                    <button v-else class="btn ok sm" type="button" @click="toggleActive(r)">
                      {{ $t("departments.actActivate") }}
                    </button>
                    <!-- C3: soft delete, click-then-confirm inline (no
                         window.confirm — it blocks the page and can't be
                         translated or laid out RTL). Suspend stays separate:
                         suspending is reversible and keeps the department in
                         the list; deleting removes it from every screen. -->
                    <template v-if="confirmingId === String(r.id)">
                      <button
                        class="btn danger sm"
                        type="button"
                        :disabled="deletingId === String(r.id)"
                        :data-test="'dept-delete-confirm-' + r.id"
                        @click="confirmDelete(r)"
                      >
                        {{ deletingId === String(r.id) ? $t("departments.delete.deleting") : $t("departments.delete.yes") }}
                      </button>
                      <button class="btn ghost sm" type="button" :disabled="deletingId === String(r.id)" @click="confirmingId = ''">
                        {{ $t("departments.delete.cancel") }}
                      </button>
                    </template>
                    <button
                      v-else
                      class="btn ghost sm"
                      type="button"
                      :title="$t('departments.delete.hint')"
                      :data-test="'dept-delete-' + r.id"
                      @click="askDelete(r)"
                    >
                      🗑️ {{ $t("departments.delete.button") }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="row between" style="padding: 11px 15px">
          <span class="muted xs">
            {{ $t("departments.summary", { count: stats.total, active: stats.active, suspended: stats.suspended }) }}
          </span>
          <span class="muted xs">{{ $t("departments.totalEmployees", { n: fInt(stats.totalEmployees) }) }}</span>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import { apiGet, apiPatch, apiDelete } from "../../api/vivo/http";
import { countByDept, shapeRows, summary, deleteErrorKey } from "./departments-vm";

export default {
  name: "VivoDepartments",
  data() {
    return {
      departments: [],
      employees: [],
      loading: true,
      error: false,
      filter: "all",
      q: "",
      // C2 filter dropdown
      filtersOpen: false,
      // C3 delete
      confirmingId: "",
      deletingId: "",
      deletedName: "",
      deleteError: null,
      // set when we arrive back from the form page (C1)
      savedNotice: null,
    };
  },
  async mounted() {
    // before the awaits — see employees.vue for why
    document.addEventListener("click", this.onDocClick);
    try {
      const [dep, emp] = await Promise.all([
        apiGet("/departments", { limit: 100 }),
        // §4 PaginationQueryDto caps limit at 100 (limit>100 → 400); the mock was
        // permissive. 100 covers per-department employee counts for the seed scale.
        apiGet("/employees", { limit: 100 }),
      ]);
      this.departments = (dep && dep.data) || [];
      this.employees = (emp && emp.data) || [];
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
    // The form page redirects back with ?saved=created|edited&name=… so the
    // list can confirm what happened. Strip it afterwards, or a refresh would
    // re-announce a save that is long done.
    const saved = this.$route.query.saved;
    if (saved === "created" || saved === "edited") {
      this.savedNotice = { kind: saved, name: String(this.$route.query.name || "") };
      this.$router.replace({ name: "departments" });
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick);
  },
  computed: {
    counts() {
      return countByDept(this.employees);
    },
    allRows() {
      return shapeRows(this.departments, this.counts);
    },
    rows() {
      // already narrowed by the server; filterRows would be a second, weaker
      // pass over a page it cannot see beyond
      return this.allRows;
    },
    stats() {
      return summary(this.allRows);
    },
    filterCount() {
      let n = 0;
      if (this.filter && this.filter !== "all") n += 1;
      if (String(this.q || "").trim()) n += 1;
      return n;
    },
  },
  methods: {
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    statusLabel(key) {
      return key === "active" ? this.$t("departments.statusActive") : this.$t("departments.statusSuspended");
    },
    async reload() {
      try {
        // C2: the status filter is applied SERVER-side (isActive on
        // DepartmentsQueryDto). It used to filter the fetched page in the
        // browser, so with the default limit of 20 the dropdown, its count
        // badge and the footer totals all described only the first 20
        // departments. `limit` is explicit for the same reason.
        const params = { limit: 100 };
        if (this.q) params.q = this.q;
        if (this.filter === "active") params.isActive = "true";
        if (this.filter === "suspended") params.isActive = "false";
        const res = await apiGet("/departments", params);
        this.departments = (res && res.data) || [];
        this.error = false;
      } catch (e) {
        this.error = true;
      }
    },
    onDocClick() {
      this.filtersOpen = false;
    },
    clearFilters() {
      this.filter = "all";
      this.q = "";
      this.reload();
    },
    // C1: add/edit are their own pages now (/departments/new, /departments/:id/edit).
    goAdd() {
      if (this.$route.name !== "departments-new") this.$router.push({ name: "departments-new" });
    },
    goEdit(row) {
      this.$router.push({ name: "departments-edit", params: { id: String(row.id) } });
    },
    askDelete(row) {
      this.confirmingId = String(row.id);
      this.deleteError = null;
      this.deletedName = "";
      this.savedNotice = null;
    },
    async confirmDelete(row) {
      const id = String(row.id);
      if (this.deletingId) return;
      this.deletingId = id;
      this.deleteError = null;
      try {
        await apiDelete("/departments/" + id);
        this.deletedName = row.nameAr;
        this.confirmingId = "";
        await this.reload();
      } catch (e) {
        // A 409 carries the employee count — pass it through so the message can
        // say HOW MANY people have to be moved first, not just "can't delete".
        const key = deleteErrorKey(e);
        const details = e && e.response && e.response.data && e.response.data.details;
        this.deleteError = { key: key, params: { n: (details && details.employees) || 0 } };
        this.confirmingId = "";
      } finally {
        this.deletingId = "";
      }
    },
    async toggleActive(row) {
      try {
        await apiPatch("/departments/" + row.id, { isActive: !row.isActive });
        await this.reload();
      } catch (e) {
        this.error = true;
      }
    },
  },
};
</script>

<style scoped>
/* C2 filters dropdown. `inset-inline-start` not `left`, so it opens from the
   toggle in both RTL and LTR. */
.filters {
  position: relative;
}
.filters__panel {
  position: absolute;
  inset-inline-start: 0;
  top: calc(100% + 6px);
  z-index: 20;
  min-width: 240px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--v-line-2);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(16, 38, 44, 0.14);
}
.filters__row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filters__row select {
  padding: 8px 10px;
  border: 1px solid var(--v-line-2);
  border-radius: 8px;
  font: inherit;
  background: #fff;
  width: 100%;
}
.filters__foot {
  border-top: 1px solid var(--v-line-2);
  padding-top: 9px;
}
</style>
